import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Receipt, Briefcase } from 'lucide-react'
import api from '../config/api'
import { useI18n } from '../hooks/useI18n'
import '../styles/Expenses.css'

function Expenses() {
  const { t } = useI18n()
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchExpenses()
  }, [])

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses')
      setExpenses(response.data.data || [])
    } catch (err) {
      console.error('Failed to load expenses:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency', currency: 'PHP', minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    const d = date._seconds ? new Date(date._seconds * 1000) : new Date(date)
    return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const categoryLabels = {
    court_filing: 'Court Filing',
    expert_witness: 'Expert Witness',
    travel: 'Travel',
    copying: 'Copying',
    postage: 'Postage',
    courier: 'Courier',
    research: 'Research',
    transcription: 'Transcription',
    process_server: 'Process Server',
    mediation: 'Mediation',
    other_professional: 'Other Professional',
    miscellaneous: 'Miscellaneous'
  }

  if (loading) {
    return <div className="expenses-loading"><div className="loading-spinner" /><p>{t('common.loading')}</p></div>
  }

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0)

  return (
    <div className="expenses-page">
      <div className="page-header">
        <h1>{t('nav.expenses') || 'Expenses'}</h1>
        <p>Billed expenses on your matters</p>
      </div>

      <motion.div className="expenses-summary" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="expenses-summary-item">
          <span className="expenses-summary-label">Total Expenses</span>
          <span className="expenses-summary-value">{expenses.length}</span>
        </div>
        <div className="expenses-summary-item">
          <span className="expenses-summary-label">Total Amount</span>
          <span className="expenses-summary-value primary">{formatCurrency(totalExpenses)}</span>
        </div>
      </motion.div>

      {expenses.length === 0 ? (
        <div className="expenses-empty">No billed expenses found</div>
      ) : (
        <div className="expenses-list">
          {expenses.map((expense, index) => (
            <motion.div key={expense.id} className="expense-item"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}>
              <div className="expense-item-left">
                <div className="expense-item-header">
                  <Receipt size={14} className="expense-icon" />
                  <span className="expense-description">{expense.description}</span>
                </div>
                <div className="expense-item-details">
                  <span className="expense-category">{categoryLabels[expense.category] || expense.category}</span>
                  {expense.matterTitle && (
                    <span className="expense-matter">
                      <Briefcase size={12} /> {expense.matterTitle}
                    </span>
                  )}
                </div>
              </div>
              <div className="expense-item-right">
                <div className="expense-amount">{formatCurrency(expense.amount)}</div>
                <div className="expense-date">{formatDate(expense.date)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Expenses
